import { SMLGenericAnalyticsType } from './genericAnalyticsTypes';
import { config } from '../../../config/config';
import { reactPixel } from './reactPixel';
import { Sentry } from '../../errorBoundary/sentry';
import { hasTrackingConsent } from '../consent/trackingConsent';
import {
  setProductEventsScreen,
  setProductEventsUser,
  startProductEvents,
  stopProductEvents,
  trackProductEvent,
} from '../events/music24Events';
import { isNativeEmbed } from '../../app/nativeAuthTokenHandoff';
import { isAnalyticsPaused } from './analyticsPause';

/**
 * Google Analytics, the Meta Pixel, Microsoft Clarity and the product events (Hernas Events API,
 * `../events/music24Events.ts`) are non-essential. They load only
 * after the visitor accepts in the consent bar (`TrackingConsentBar`), never before: `setup()` at
 * boot starts them only when a stored consent exists, and `enableTracking()` starts them on Accept.
 * Until then every call below does nothing. Plausible (cookieless) is separate: `PlausibleTracker`.
 */
let trackingOn = false;
const startGoogleAnalytics = (): void => {
  if (!config.gTagID) {
    return;
  }
  window.dataLayer = window.dataLayer || [];
  function gtag() {
    // gtag.js reads the `arguments` object, not an array.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  }
  window.gtag = gtag;
  // The visitor accepted, so storage starts granted (Google consent mode v2).
  window.gtag('consent', 'default', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  window.gtag('js', new Date());

  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${config.gTagID}`;
  document.head.appendChild(script);
};

const startClarity = (): void => {
  // Production only — config.clarityID is empty everywhere else (staging, beta, local).
  if (!config.clarityID) {
    return;
  }
  /* eslint-disable no-multi-assign,@typescript-eslint/strict-boolean-expressions,prefer-rest-params,func-names,no-plusplus */
  (function (c: any, l: Document, a: string, r: string, i: string) {
    c[a] =
      c[a] ||
      function () {
        (c[a].q = c[a].q || []).push(arguments);
      };
    const t = l.createElement(r) as any;
    t.async = 1;
    t.src = `https://www.clarity.ms/tag/${i}`;
    const y = l.getElementsByTagName(r)[0] as any;
    y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', config.clarityID);
  /* eslint-enable no-multi-assign,@typescript-eslint/strict-boolean-expressions,prefer-rest-params,func-names,no-plusplus */
  // Clarity's cookie consent. Sent only here, after Accept.
  window.clarity('consent');
};

/** False before consent, and for the whole page view of an admin session (`analyticsPause.ts`). */
const isLive = (): boolean => trackingOn && !isAnalyticsPaused();

const gtagCall = (...args: unknown[]): void => {
  if (isLive() && typeof window.gtag === 'function') {
    window.gtag(...args);
  }
};

export const SMLGenericAnalytics: SMLGenericAnalyticsType = {
  async screen(name) {
    if (!isLive()) {
      return;
    }
    setProductEventsScreen(name);
    trackProductEvent('screen_viewed', { screen: name });
    await window.analytics?.page(name);

    gtagCall('config', config.gTagID, { page_path: name });
    if (config.fbPixelID) {
      reactPixel.pageView();
    }
  },
  async track(event, data) {
    if (!isLive()) {
      return;
    }
    trackProductEvent(event, data);
    await window.analytics?.track(event, data);

    gtagCall('event', event, data);
    if (config.fbPixelID) {
      reactPixel.trackCustom(event, data);
    }
  },
  async identifyAnonymous(aId: string) {
    if (!isLive()) {
      return;
    }
    await window.analytics?.identify({
      anonymousIdentifier: aId,
    });
    gtagCall('config', config.gTagID, {
      user_id: aId,
    });
    window.clarity?.('identify', aId);
  },
  async alias(userId: string, aId: string) {
    if (!isLive()) {
      return;
    }
    await window.analytics?.alias(aId, userId);
  },
  async identify(user, licenseId, aId: string) {
    if (!isLive()) {
      return;
    }
    setProductEventsUser(user);
    await window.analytics?.identify(user, { licenseId, anonymousIdentifier: aId });
    await window.analytics?.group(licenseId, { anonymousIdentifier: aId });
    // Clarity's identify calls String.replace on the id; a number throws inside clarity.js
    // (MUSIC24-WEB-4).
    window.clarity?.('identify', `${user}`);
    gtagCall('config', config.gTagID, {
      user_id: user,
      licenseId,
    });
  },
  sentryIdentity(userId, accountId, licenseId) {
    Sentry.setUser({ userId, accountId, licenseId });
  },
  setup(): Promise<void> {
    if (hasTrackingConsent()) {
      SMLGenericAnalytics.enableTracking();
    }
    return Promise.resolve();
  },
  enableTracking() {
    // A native app's web view loads no non-essential tracker (the native app has its own).
    if (trackingOn || (typeof navigator !== 'undefined' && isNativeEmbed(navigator.userAgent))) {
      return;
    }
    // An admin session loads no tracker either: Clarity records a session as soon as it loads.
    if (isAnalyticsPaused()) {
      return;
    }
    trackingOn = true;
    startGoogleAnalytics();
    if (config.fbPixelID) {
      reactPixel.init(config.fbPixelID);
      reactPixel.grantConsent();
    }
    startClarity();
    startProductEvents();
  },
  disableTracking() {
    if (!trackingOn) {
      return;
    }
    window.clarity?.('consent', false);
    window.gtag?.('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
    if (config.fbPixelID) {
      reactPixel.revokeConsent();
    }
    stopProductEvents();
    trackingOn = false;
  },
  isTrackingOn() {
    return trackingOn;
  },
  clearIdentity() {
    setProductEventsUser(undefined);
  },
};
